/**
 * Card displaying a single requirement's details with edit, comments, attachments.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AutoComplete, Card, Typography, Space, Descriptions, Button, Collapse, Modal, message, Tag, Select, Tooltip, Popconfirm } from 'antd';
import {
  MessageOutlined,
  PaperClipOutlined,
  EditOutlined,
  HistoryOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  NotificationOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { deleteRequirement, getRequirementDeleteErrorMessage, getRequirementSource, listProjectTags, listProjectTags1, listProjectTags2, updateRequirementSource, updateRequirementStatus, updateRequirementTag, updateRequirementTag1, updateRequirementTag2 } from '@/api/requirements';
import { useAuth } from '@/hooks/useAuth';
import { useT, t as tGlobal } from '@/i18n';
import { getBspTagColor } from '@/utils/tagColor';
import { localeFor } from '@/utils';
import RequirementEditor from './RequirementEditor';
import RequirementComments from './RequirementComments';
import RequirementAttachments from './RequirementAttachments';
import RequirementLogModal from './RequirementLogModal';
import { hasNotifyEntry } from './notifyList';
import type { Requirement } from '@/types';
import FlowbackButton from '../flowback/FlowbackButton';

const { Text, Paragraph } = Typography;

interface RequirementCardProps {
  requirement: Requirement;
  projectId: number;
  canEdit?: boolean;
  canDelete?: boolean;
  onUpdated?: (updated: Requirement) => void;
  onDeleted?: (reqId: string) => void;
  notifyList?: Set<string>;
  onToggleNotifyList?: (reqId: string) => void;
  onOpenNotifyModal?: () => void;
}

const displayValue = (val: string | null | undefined) => val || tGlobal('未指定');

const RequirementCard: React.FC<RequirementCardProps> = ({
  requirement: initialRequirement,
  projectId,
  canEdit,
  canDelete,
  onUpdated,
  onDeleted,
  notifyList,
  onToggleNotifyList,
  onOpenNotifyModal,
}) => {
  const { t, lang } = useT();
  const navigate = useNavigate();
  const { isExternal } = useAuth();
  const [requirement, setRequirement] = useState(initialRequirement);
  const [editing, setEditing] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Inline status editing (external users)
  const [editingStatus, setEditingStatus] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);

  // Inline TAG editing (external users)
  const [editingTag, setEditingTag] = useState(false);
  const [tagValue, setTagValue] = useState(requirement.bspTag || '');
  const tagSavedValue = useRef(requirement.bspTag || '');
  const [projectTags, setProjectTags] = useState<string[]>([]);

  const [tagValue1, setTagValue1] = useState(requirement.bspTag1 || '');
  const tagSavedValue1 = useRef(requirement.bspTag1 || '');
  const [editingTag1, setEditingTag1] = useState(false);
  const [projectTags1, setProjectTags1] = useState<string[]>([]);

  const [tagValue2, setTagValue2] = useState(requirement.bspTag2 || '');
  const tagSavedValue2 = useRef(requirement.bspTag2 || '');
  const [editingTag2, setEditingTag2] = useState(false);
  const [projectTags2, setProjectTags2] = useState<string[]>([]);

  // Source (需求来源) editing — internal users only
  const [editingSource, setEditingSource] = useState(false);
  const [sourceType, setSourceType] = useState(requirement.sourceType || 'mainline');
  const [customerReqIds, setCustomerReqIds] = useState<string[]>(requirement.customerReqIds || []);
  const [sourceSaving, setSourceSaving] = useState(false);
  const sourceSavedRef = useRef({ type: requirement.sourceType || 'mainline', ids: requirement.customerReqIds || [] });

  // Fetch source on mount
  useEffect(() => {
    getRequirementSource(projectId, requirement.reqId).then(res => {
      setSourceType(res.source_type);
      setCustomerReqIds(res.customer_req_ids);
      sourceSavedRef.current = { type: res.source_type, ids: res.customer_req_ids };
    }).catch(() => {});
  }, [projectId, requirement.reqId]);

  // Sync local state when parent provides updated data (e.g., store refresh)
  useEffect(() => {
    if (!editing) {
      setRequirement(initialRequirement);
      const savedTag = initialRequirement.bspTag || '';
      setTagValue(savedTag);
      tagSavedValue.current = savedTag;
      const t1 = initialRequirement.bspTag1 || '';
      setTagValue1(t1);
      tagSavedValue1.current = t1;
      const t2 = initialRequirement.bspTag2 || '';
      setTagValue2(t2);
      tagSavedValue2.current = t2;
    }
  }, [initialRequirement.linkedPackageId, initialRequirement.status, initialRequirement.description, initialRequirement.deliveryStage, initialRequirement.bspTag, initialRequirement.bspTag1, initialRequirement.bspTag2]);

  const {
    reqId,
    description,
    owner,
    updatedAt,
    deliveryStage,
    deliverables,
    verificationMethod,
    status,
    priority,
    remark,
    commentCount,
    attachmentCount,
    linkedPackageId,
    linkedPackageName,
    attachment,
    bspTag,
    bspTag1,
    bspTag2,
  } = requirement;

  const handleSaved = (updated: Requirement) => {
    setRequirement(updated);
    setEditing(false);
    onUpdated?.(updated);
  };

  const handleStatusChange = async (newStatus: string) => {
    setStatusSaving(true);
    try {
      await updateRequirementStatus(projectId, requirement.reqId, {
        id: requirement.id,
        subcategory: requirement.subcategory,
        status: newStatus,
      });
      const updated = { ...requirement, status: newStatus };
      setRequirement(updated);
      setEditingStatus(false);
      onUpdated?.(updated);
    } catch {
      message.error(t('状态更新失败'));
    } finally {
      setStatusSaving(false);
    }
  };

  const handleTagBlur = async () => {
    const trimmed = tagValue.trim();
    setEditingTag(false);
    if (trimmed === tagSavedValue.current) return;
    try {
      await updateRequirementTag(projectId, requirement.reqId, trimmed);
      tagSavedValue.current = trimmed;
      const updated = { ...requirement, bspTag: trimmed || null };
      setRequirement(updated);
      onUpdated?.(updated);
    } catch {
      message.error(t('TAG保存失败'));
    }
  };

  const handleTagBlur1 = async () => {
    const trimmed = tagValue1.trim();
    setEditingTag1(false);
    if (trimmed === tagSavedValue1.current) return;
    try {
      await updateRequirementTag1(projectId, requirement.reqId, trimmed);
      tagSavedValue1.current = trimmed;
      const updated = { ...requirement, bspTag1: trimmed || null };
      setRequirement(updated);
      onUpdated?.(updated);
    } catch {
      message.error(t('TAG1保存失败'));
    }
  };

  const handleTagBlur2 = async () => {
    const trimmed = tagValue2.trim();
    setEditingTag2(false);
    if (trimmed === tagSavedValue2.current) return;
    try {
      await updateRequirementTag2(projectId, requirement.reqId, trimmed);
      tagSavedValue2.current = trimmed;
      const updated = { ...requirement, bspTag2: trimmed || null };
      setRequirement(updated);
      onUpdated?.(updated);
    } catch {
      message.error(t('TAG2保存失败'));
    }
  };

  const handleSourceSave = async () => {
    setSourceSaving(true);
    try {
      const res = await updateRequirementSource(projectId, requirement.reqId, {
        source_type: sourceType,
        customer_req_ids: sourceType === 'customer' ? customerReqIds : [],
      });
      const updated = {
        ...requirement,
        sourceType: res.source_type,
        customerReqIds: res.customer_req_ids,
      };
      setRequirement(updated);
      setSourceType(res.source_type);
      setCustomerReqIds(res.customer_req_ids);
      sourceSavedRef.current = { type: res.source_type, ids: res.customer_req_ids };
      setEditingSource(false);
      onUpdated?.(updated);
    } catch {
      message.error(t('需求来源保存失败'));
    } finally {
      setSourceSaving(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: t('确认删除需求？'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <>
          <p>
            {t('将删除需求:')} <strong>{reqId}</strong>
          </p>
          <p style={{ color: '#ff4d4f', marginTop: 8 }}>
            {t('此操作将同时删除该需求的所有评论和附件，且不可恢复。')}
          </p>
        </>
      ),
      okText: t('确认删除'),
      okType: 'danger' as const,
      cancelText: t('取消'),
      onOk: async () => {
        setDeleteLoading(true);
        try {
          const result = await deleteRequirement(projectId, reqId);
          message.success(t('需求已删除'));
          if (result?.deleted_from_projects?.length) {
            Modal.info({
              title: t('主线同步提示'),
              content: t('主线项目 {projects} 包含该需求，已自动删除', { projects: result.deleted_from_projects.join(lang === 'en' ? ', ' : '、') }),
            });
          }
          onDeleted?.(reqId);
        } catch (error: unknown) {
          setDeleteLoading(false);
          message.error(getRequirementDeleteErrorMessage(error));
        }
      },
    });
  };

  const collapseItems = [
    {
      key: 'comments',
      label: (
        <Space>
          <MessageOutlined />
          {t('评论')} {commentCount > 0 && `(${commentCount})`}
        </Space>
      ),
      children: (
        <RequirementComments projectId={projectId} requirementId={reqId} />
      ),
    },
    {
      key: 'attachments',
      label: (
        <Space>
          <PaperClipOutlined />
          {t('附件')} {attachmentCount > 0 && `(${attachmentCount})`}
        </Space>
      ),
      children: (
        <RequirementAttachments projectId={projectId} requirementId={reqId} />
      ),
    },
  ];

  return (
    <Card
      size="small"
      className="mb-3"
      title={
        // Wrapping header row: in a narrow detail panel the actions move to the
        // next line instead of squeezing the requirement id to zero width.
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            columnGap: 12,
            rowGap: 4,
            width: '100%',
            whiteSpace: 'normal',
          }}
        >
          <Text strong>{reqId}</Text>
          <Space
            size="middle"
            wrap
            style={{ marginLeft: 'auto', justifyContent: 'flex-end' }}
          >
            <FlowbackButton
              projectId={projectId}
              requirementId={requirement.reqId}
              subcategory={requirement.subcategory}
            />
            <Button
              type="text"
              size="small"
              icon={<HistoryOutlined />}
              onClick={() => setLogOpen(true)}
            >
              {t('日志')}
            </Button>
            <Tooltip title={!canEdit ? t('无需求编辑权限') : undefined}>
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                disabled={!canEdit}
                onClick={() => setEditing(!editing)}
              >
                {editing ? t('取消') : t('编辑')}
              </Button>
            </Tooltip>
            {!isExternal && notifyList !== undefined && onToggleNotifyList && onOpenNotifyModal && (
              <>
                {hasNotifyEntry(notifyList, projectId, reqId) ? (
                  <Button
                    type="text"
                    size="small"
                    icon={<NotificationOutlined />}
                    onClick={onOpenNotifyModal}
                  >
                    {t('通知列表({count})', { count: notifyList.size })}
                  </Button>
                ) : (
                  <Popconfirm
                    title={t('将该需求加入通知列表？')}
                    onConfirm={() => onToggleNotifyList(reqId)}
                    okText={t('确认')}
                    cancelText={t('取消')}
                  >
                    <Button
                      type="text"
                      size="small"
                      icon={<PlusOutlined />}
                    >
                      {t('通知列表')}
                    </Button>
                  </Popconfirm>
                )}
              </>
            )}
            <Tooltip title={!canEdit ? t('无需求编辑权限') : undefined}>
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                loading={deleteLoading}
                disabled={!(canDelete ?? canEdit)}
                onClick={handleDelete}
              >
                {t('删除')}
              </Button>
            </Tooltip>
          </Space>
        </div>
      }
    >
      {editing ? (
        <RequirementEditor
          projectId={projectId}
          requirement={requirement}
          onSaved={handleSaved}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <>
          <Paragraph className="mb-2" style={{ whiteSpace: 'pre-wrap' }}>
            {displayValue(description)}
          </Paragraph>
          <Descriptions size="small" column={2} bordered={false}>
            <Descriptions.Item label={t('责任人')}>{displayValue(owner)}</Descriptions.Item>
            <Descriptions.Item label={t('更新时间')}>
              {updatedAt ? new Date(updatedAt).toLocaleString(localeFor(lang)) : t('未指定')}
            </Descriptions.Item>
            <Descriptions.Item label={t('交付阶段')}>{displayValue(deliveryStage)}</Descriptions.Item>
            <Descriptions.Item label={t('一级分类')}>{displayValue(requirement.category)}</Descriptions.Item>
            <Descriptions.Item label={t('二级分类')}>{displayValue(requirement.category2)}</Descriptions.Item>
            <Descriptions.Item label={t('优先级')}>
              {priority ? (
                <Tag color={priority === '必选' ? 'green' : 'default'}>
                  {priority}
                </Tag>
              ) : (
                <Text type="secondary">{t('未指定')}</Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('交付物')}>{displayValue(deliverables)}</Descriptions.Item>
            <Descriptions.Item label={t('设计文档')}>
              {attachment ? (
                <Space wrap>
                  {(() => {
                    const urls = attachment
                      .split(/[,，]/)
                      .map(url => url.trim())
                      .filter(url => url.length > 0);

                    return urls.map((url, index) => (
                      <a
                        key={index}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#1890ff' }}
                      >
                        {urls.length > 1 ? t('查看文档 {index}', { index: index + 1 }) : t('查看文档')}
                      </a>
                    ));
                  })()}
                </Space>
              ) : (
                <Text type="secondary">{t('未指定')}</Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('验证方法')}>{displayValue(verificationMethod)}</Descriptions.Item>
            <Descriptions.Item label={t('需求交付首版软件')}>
              {linkedPackageId ? (
                <a onClick={() => navigate(`/package/${linkedPackageId}`)}>{linkedPackageName}</a>
              ) : (
                t('未指定')
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('状态')}>
              {editingStatus ? (
                <Select
                  size="small"
                  autoFocus
                  open
                  value={status || undefined}
                  placeholder={t('选择状态')}
                  allowClear
                  loading={statusSaving}
                  style={{ minWidth: 160 }}
                  onChange={handleStatusChange}
                  onBlur={() => setEditingStatus(false)}
                  options={[
                    { value: 'released', label: t('released (供应商已接收)') },
                    { value: 'reject', label: t('reject (已拒绝)') },
                  ]}
                />
              ) : (
                <Space size={4}>
                  <span>{displayValue(status)}</span>
                  <Tooltip title={t('修改状态')}>
                    <EditOutlined
                      style={{ fontSize: 12, color: '#1677ff', cursor: 'pointer' }}
                      onClick={() => setEditingStatus(true)}
                    />
                  </Tooltip>
                </Space>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="TAG">
              {editingTag ? (
                <AutoComplete
                  size="small"
                  autoFocus
                  value={tagValue}
                  placeholder={t('输入或选择TAG')}
                  style={{ minWidth: 160 }}
                  options={projectTags
                    .filter(t => !tagValue || t.toLowerCase().includes(tagValue.toLowerCase()))
                    .map(t => ({ value: t }))}
                  onChange={setTagValue}
                  onBlur={handleTagBlur}
                  onSelect={(v: string) => { setTagValue(v); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleTagBlur(); }}
                  maxLength={100}
                  allowClear
                />
              ) : (
                <Space size={4}>
                  {bspTag ? (
                    <Tag color={getBspTagColor(bspTag)} style={{ margin: 0 }}>{bspTag}</Tag>
                  ) : (
                    <Text type="secondary">{t('未设置')}</Text>
                  )}
                  <Tooltip title={t('修改TAG')}>
                    <EditOutlined
                      style={{ fontSize: 12, color: '#1677ff', cursor: 'pointer' }}
                      onClick={() => {
                        setTagValue(requirement.bspTag || '');
                        setEditingTag(true);
                        listProjectTags(projectId).then(setProjectTags).catch(() => {});
                      }}
                    />
                  </Tooltip>
                </Space>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="TAG1">
              {editingTag1 ? (
                <AutoComplete
                  size="small"
                  autoFocus
                  value={tagValue1}
                  placeholder={t('输入或选择TAG1')}
                  style={{ minWidth: 160 }}
                  options={projectTags1
                    .filter(t => !tagValue1 || t.toLowerCase().includes(tagValue1.toLowerCase()))
                    .map(t => ({ value: t }))}
                  onChange={setTagValue1}
                  onBlur={handleTagBlur1}
                  onSelect={(v: string) => { setTagValue1(v); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleTagBlur1(); }}
                  maxLength={100}
                  allowClear
                />
              ) : (
                <Space size={4}>
                  {bspTag1 ? (
                    <Tag color={getBspTagColor(bspTag1)} style={{ margin: 0 }}>{bspTag1}</Tag>
                  ) : (
                    <Text type="secondary">{t('未设置')}</Text>
                  )}
                  <Tooltip title={t('修改TAG1')}>
                    <EditOutlined
                      style={{ fontSize: 12, color: '#1677ff', cursor: 'pointer' }}
                      onClick={() => {
                        setTagValue1(requirement.bspTag1 || '');
                        setEditingTag1(true);
                        listProjectTags1(projectId).then(setProjectTags1).catch(() => {});
                      }}
                    />
                  </Tooltip>
                </Space>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="TAG2">
              {editingTag2 ? (
                <AutoComplete
                  size="small"
                  autoFocus
                  value={tagValue2}
                  placeholder={t('输入或选择TAG2')}
                  style={{ minWidth: 160 }}
                  options={projectTags2
                    .filter(t => !tagValue2 || t.toLowerCase().includes(tagValue2.toLowerCase()))
                    .map(t => ({ value: t }))}
                  onChange={setTagValue2}
                  onBlur={handleTagBlur2}
                  onSelect={(v: string) => { setTagValue2(v); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleTagBlur2(); }}
                  maxLength={100}
                  allowClear
                />
              ) : (
                <Space size={4}>
                  {bspTag2 ? (
                    <Tag color={getBspTagColor(bspTag2)} style={{ margin: 0 }}>{bspTag2}</Tag>
                  ) : (
                    <Text type="secondary">{t('未设置')}</Text>
                  )}
                  <Tooltip title={t('修改TAG2')}>
                    <EditOutlined
                      style={{ fontSize: 12, color: '#1677ff', cursor: 'pointer' }}
                      onClick={() => {
                        setTagValue2(requirement.bspTag2 || '');
                        setEditingTag2(true);
                        listProjectTags2(projectId).then(setProjectTags2).catch(() => {});
                      }}
                    />
                  </Tooltip>
                </Space>
              )}
            </Descriptions.Item>
            {!isExternal && (
              <Descriptions.Item label={t('需求来源')} span={2}>
                {editingSource ? (
                  <Space orientation="vertical" size={4} style={{ width: '100%' }}>
                    <Select
                      size="small"
                      autoFocus
                      value={sourceType}
                      style={{ minWidth: 160 }}
                      onChange={(val) => {
                        setSourceType(val);
                        if (val === 'mainline') setCustomerReqIds([]);
                      }}
                      options={[
                        { value: 'mainline', label: t('主线') },
                        { value: 'customer', label: t('客户原始需求') },
                      ]}
                    />
                    {sourceType === 'customer' && (
                      <Select
                        mode="tags"
                        size="small"
                        placeholder={t('输入客户原始需求ID，回车确认')}
                        value={customerReqIds}
                        onChange={(vals) => setCustomerReqIds(vals)}
                        style={{ minWidth: 200 }}
                        tokenSeparators={[',', ' ']}
                      />
                    )}
                    <Space size={4}>
                      <Button size="small" type="primary" loading={sourceSaving} onClick={handleSourceSave}>
                        {t('保存')}
                      </Button>
                      <Button size="small" onClick={() => {
                        setEditingSource(false);
                        setSourceType(sourceSavedRef.current.type);
                        setCustomerReqIds([...sourceSavedRef.current.ids]);
                      }}>
                        {t('取消')}
                      </Button>
                    </Space>
                  </Space>
                ) : (
                  <Space size={4}>
                    <span>
                      {sourceType === 'customer'
                        ? t('客户原始需求 ({ids})', { ids: (customerReqIds || []).join(', ') })
                        : t('主线')}
                    </span>
                    <Tooltip title={t('修改需求来源')}>
                      <EditOutlined
                        style={{ fontSize: 12, color: '#1677ff', cursor: 'pointer' }}
                        onClick={() => {
                          setEditingSource(true);
                        }}
                      />
                    </Tooltip>
                  </Space>
                )}
              </Descriptions.Item>
            )}
            <Descriptions.Item label={t('验证准则')} span={2}>
              <span style={{ whiteSpace: 'pre-wrap' }}>
                {requirement.validationCriteria || '-'}
              </span>
            </Descriptions.Item>
            <Descriptions.Item label={t('备注')}>
              <Paragraph
                ellipsis={{ rows: 2, expandable: true, symbol: t('展开') }}
                style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}
              >
                {remark || <Text type="secondary">{t('未指定')}</Text>}
              </Paragraph>
            </Descriptions.Item>
          </Descriptions>
        </>
      )}

      <Collapse
        ghost
        size="small"
        items={collapseItems}
        className="mt-2"
      />

      <RequirementLogModal
        open={logOpen}
        projectId={projectId}
        requirementId={reqId}
        onClose={() => setLogOpen(false)}
      />
    </Card>
  );
};

export default RequirementCard;
